import { useEffect, useState, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import { Menu, X, Phone, Mail, MessageCircle, ChevronDown, Archive, Mic, Search, Crown } from "lucide-react";
import { Toaster, toast } from "sonner";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// User's archival images for hero slideshow
const HERO_SLIDESHOW = [
  "https://customer-assets.emergentagent.com/job_angry-satoshi-5/artifacts/hgotxoee_st11.jpg", // Turban family
  "https://customer-assets.emergentagent.com/job_angry-satoshi-5/artifacts/no5ehb65_Divya%20Gauri.jpg", // Bengal family
  "https://customer-assets.emergentagent.com/job_angry-satoshi-5/artifacts/aiklc1qw_some-of-my-family-pictures-from-the-early-80s-from-india-v0-y8h7922fo7gb1.jpg", // 80s colorful family
  "https://customer-assets.emergentagent.com/job_angry-satoshi-5/artifacts/fumaaem0_photo6-1.webp", // Large group B&W
];

// Other user images
const USER_IMAGES = {
  cooking: "https://customer-assets.emergentagent.com/job_angry-satoshi-5/artifacts/1uhmgf2s_Rajyashri%20Goody.jpg",
  hero3: "https://customer-assets.emergentagent.com/job_angry-satoshi-5/artifacts/no5ehb65_Divya%20Gauri.jpg",
};

// Stock images
const STOCK_IMAGES = {
  documents: "https://images.unsplash.com/photo-1532154286208-09c66db54ac5?crop=entropy&cs=srgb&fm=jpg&q=85",
  letters: "https://images.unsplash.com/photo-1769791367981-8ca8e0d400a8?crop=entropy&cs=srgb&fm=jpg&q=85",
  heirloom: "https://images.unsplash.com/photo-1684771003097-9e7f2e7b15ae?crop=entropy&cs=srgb&fm=jpg&q=85"
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const imageReveal = {
  hidden: { filter: "blur(10px) grayscale(100%)", opacity: 0 },
  visible: { filter: "blur(0px) grayscale(0%)", opacity: 1, transition: { duration: 1.2 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}>

      {children}
    </motion.div>);

};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
  { href: "#about", label: "About" },
  { href: "#services", label: "Explore" },
  { href: "#contact", label: "Contact" }];


  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "glass-nav shadow-sm py-4" : "py-6 bg-transparent"}`
        }>

        <div className="w-full pl-12 md:pl-20 lg:pl-20 pr-6 md:pr-12 flex items-center justify-between">
          <a
            href="#hero"
            onClick={(e) => {e.preventDefault();scrollToSection("#hero");}}
            className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#2C2420]"
            data-testid="logo-link">

            Yaadein
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => {e.preventDefault();scrollToSection(link.href);}}
              className="nav-link-hero"
              data-testid={`nav-${link.label.toLowerCase()}`}>

                {link.label}
              </a>
            )}
            <button
              onClick={() => scrollToSection("#contact")}
              className="btn-primary"
              data-testid="nav-cta-button">

              Begin Your Journey
            </button>
          </div>
          
          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle">

            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>
      
      {/* Mobile Menu */}
      {isMobileMenuOpen &&
      <div className="mobile-menu-overlay" data-testid="mobile-menu">
          {navLinks.map((link) =>
        <a
          key={link.href}
          href={link.href}
          onClick={(e) => {e.preventDefault();scrollToSection(link.href);}}
          className="font-heading text-3xl"
          data-testid={`mobile-nav-${link.label.toLowerCase()}`}>

              {link.label}
            </a>
        )}
          <button
          onClick={() => scrollToSection("#contact")}
          className="btn-primary mt-8"
          data-testid="mobile-cta-button">

            Begin Your Journey
          </button>
        </div>
      }
    </>);

};

// Hero Section with Full-Bleed Crossfade Slideshow
const HeroSection = () => {
  const [currentImage, setCurrentImage] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % HERO_SLIDESHOW.length);
    }, 7000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <section id="hero" className="min-h-screen relative overflow-hidden" data-testid="hero-section">
      {/* Background Slideshow Images */}
      <div className="absolute inset-0 z-0">
        {HERO_SLIDESHOW.map((src, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-2500 ease-in-out ${
              currentImage === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={src}
              alt={`Family memory ${index + 1}`}
              className="w-full h-full object-cover sepia-subtle"
              data-testid={`hero-bg-image-${index}`}
            />
          </div>
        ))}
        {/* Dark overlay for image */}
        <div className="absolute inset-0 bg-[#2C2420]/25 z-10" />
      </div>
      
      {/* Gradient overlay - cream fade from left */}
      <div 
        className="absolute inset-0 z-20"
        style={{
          background: "linear-gradient(to right, rgba(253, 251, 247, 0.97) 0%, rgba(253, 251, 247, 0.92) 30%, rgba(253, 251, 247, 0.7) 50%, rgba(253, 251, 247, 0.3) 70%, rgba(253, 251, 247, 0) 100%)"
        }}
      />
      
      {/* Content */}
      <div className="relative z-30 min-h-screen flex items-center">
        <div className="w-full pl-12 md:pl-20 lg:pl-20 pr-6 py-32">
          <AnimatedSection className="max-w-2xl">
            <motion.h1
              variants={fadeInUp}
              className="hero-tagline text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] mb-6 text-[#2C2420] text-left"
              data-testid="hero-headline">
              Every Family
              <br />
              Has a Story
              <br />
              <span className="text-[#8B4513]">Worth Keeping</span>
            </motion.h1>
            
            <motion.p
              variants={fadeInUp}
              className="font-body text-lg md:text-xl leading-relaxed opacity-80 max-w-[520px] mb-10 text-[#2C2420] text-left">
              We preserve the voices, photographs, and heirlooms that 
              connect generations—transforming scattered memories into 
              lasting legacies your family will treasure forever.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <button
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-primary"
                data-testid="hero-cta-primary">
                Start Preserving
              </button>
              <button
                onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-secondary"
                data-testid="hero-cta-secondary">
                Explore
              </button>
            </motion.div>
          </AnimatedSection>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="hidden md:flex absolute bottom-12 left-1/2 -translate-x-1/2 flex-col items-center gap-2 z-30">
        <span className="font-mono text-xs uppercase tracking-widest opacity-40 text-[#2C2420]">Scroll</span>
        <ChevronDown size={20} className="opacity-40 animate-bounce text-[#2C2420]" />
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 md:py-32 bg-[#F3EFE7] relative" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <motion.h2
                variants={fadeInUp}
                className="font-heading text-4xl md:text-5xl font-light mb-8"
                data-testid="about-headline">
                Memories Fade.
                <br />
                <span className="text-[#8B4513]">Legacies Endure.</span>
              </motion.h2>
              
              <motion.p
                variants={fadeInUp}
                className="text-lg leading-relaxed opacity-80 mb-6">
                '<em>Yaadein</em>' means memories in Hindi and Urdu—and that's exactly what we help families preserve. We are a dedicated team of archivists, historians, and storytellers who believe that every photograph, every letter, and every family recipe holds a piece of history worth saving.
              </motion.p>
              
              <motion.p
                variants={fadeInUp}
                className="text-lg leading-relaxed opacity-80">
                From digitizing fading photographs to recording the voices of 
                your elders, from tracing your ancestral roots to creating 
                museum-quality heirloom books—we transform scattered memories 
                into cohesive, beautiful narratives that will be treasured for 
                generations.
              </motion.p>
            </div>
            
            <motion.div variants={imageReveal} className="relative">
              <div className="photo-frame-rotated">
                <img
                  src={USER_IMAGES.cooking}
                  alt="Family traditions being passed down"
                  className="w-full"
                  data-testid="about-image" />

              </div>
              <div className="absolute -bottom-8 -left-8 bg-[#2C2420] text-[#FDFBF7] p-6 max-w-xs">
                <p className="font-heading text-xl italic">
                  "The stories we don't tell are the ones that get forgotten."
                </p>
              </div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </section>);

};

// Service Category Component with Accordion
const ServiceCategory = ({ number, icon: Icon, title, subtitle, description, services, image, imageAlt, reversed = false }) => {
  return (
    <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-start ${reversed ? "lg:flex-row-reverse" : ""}`}>
      {/* Content */}
      <div className={reversed ? "lg:order-2" : ""}>
        <AnimatedSection>
          <motion.div variants={fadeInUp} className="relative mb-6">
            <span className="section-number">{number}</span>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-none bg-[#2C2420] flex items-center justify-center">
                <Icon size={24} className="text-[#FDFBF7]" />
              </div>
              <p className="font-mono text-sm uppercase tracking-widest opacity-60">{subtitle}</p>
            </div>
            <h3 className="font-heading text-3xl md:text-4xl font-light">{title}</h3>
          </motion.div>
          
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed opacity-80 mb-6">
            {description}
          </motion.p>
          
          <motion.div variants={fadeInUp}>
            <Accordion type="single" collapsible className="border-t border-[#D8CFC4]">
              {services.map((service, idx) => (
                <AccordionItem 
                  key={idx} 
                  value={`item-${idx}`}
                  className="border-b border-[#D8CFC4]"
                >
                  <AccordionTrigger 
                    className="py-4 hover:no-underline group"
                    data-testid={`accordion-trigger-${number}-${idx}`}
                  >
                    <span className="font-heading text-xl text-left group-hover:text-[#8B4513] transition-colors">
                      {service.name}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm opacity-70 leading-relaxed pb-4">
                    {service.description}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </AnimatedSection>
      </div>
      
      {/* Image */}
      <div className={reversed ? "lg:order-1" : ""}>
        <AnimatedSection>
          <motion.div variants={imageReveal}>
            <div className={reversed ? "photo-frame-rotated-neg" : "photo-frame-rotated"}>
              <img
                src={image}
                alt={imageAlt}
                className="w-full sepia-subtle"
                data-testid={`service-image-${number}`}
              />
            </div>
          </motion.div>
        </AnimatedSection>
      </div>
    </div>
  );
};

// Services Section
const ServicesSection = () => {
  const serviceCategories = [
  {
    number: "01",
    icon: Archive,
    title: "Physical & Digital Preservation",
    subtitle: "Archival Services",
    description: "We convert your physical memories into secure, searchable digital archives while protecting the originals through professional conservation.",
    services: [
    {
      name: "Archival Digitization & Cataloging",
      description: "High-resolution scanning of photos, slides, and documents with meticulous metadata tagging for easy navigation."
    },
    {
      name: "Physical Conservation & Stabilization",
      description: "Professional archival housing, film negative development, and climate-controlled storage guidance."
    },
    {
      name: "The Digital Audit",
      description: "Comprehensive gathering of scattered digital files from old drives and cloud accounts into one unified library."
    }],

    image: STOCK_IMAGES.documents,
    imageAlt: "Vintage documents and photographs"
  },
  {
    number: "02",
    icon: Mic,
    title: "Oral History & Narrative Storytelling",
    subtitle: "Voice & Story",
    description: "Capture the irreplaceable voices and stories of your family elders before they're lost to time, preserving first-hand accounts for future generations.",
    services: [
    {
      name: "Guided Oral History Interviews",
      description: "Professional documentarians conduct in-depth interviews to capture stories that names and dates alone can't tell."
    },
    {
      name: '"Living Memory" Slideshows',
      description: "Dynamic audio-visual experiences pairing family photos with recorded voice-overs describing memories in real-time."
    },
    {
      name: "Translation & Transcription",
      description: "Bridge generations by translating old family letters, poems, and diaries to keep ancestral sentiments accessible."
    }],

    image: USER_IMAGES.hero3,
    imageAlt: "Family heritage portrait"
  },
  {
    number: "03",
    icon: Search,
    title: "Research & Ancestry Mapping",
    subtitle: "Discovery & Heritage",
    description: "Go beyond names on a chart to discover the geographical journeys, historical contexts, and hidden stories of your ancestors.",
    services: [
    {
      name: "Genealogy & Tree Building",
      description: "Robust family tree construction with verified lineages and connections between generations."
    },
    {
      name: "Migration & Legacy Mapping",
      description: "Custom map visualizations tracing your family's movement across borders and through history."
    },
    {
      name: "Deep-Dive Investigative Research",
      description: "External research using archives, old newspapers, and local records to trace ancestral homes, businesses, and heirlooms."
    }],

    image: STOCK_IMAGES.letters,
    imageAlt: "Historical letters and documents"
  },
  {
    number: "04",
    icon: Crown,
    title: "Heirloom Collections & Exhibits",
    subtitle: "Legacy Creations",
    description: "Transform your family archives into museum-quality heirlooms—from bespoke coffee table books to documentary films.",
    services: [
    {
      name: "The Legacy Suite",
      description: "Bespoke coffee table books, feature-length documentary films, or private family exhibitions—the crown jewels of your collection."
    },
    {
      name: "The Family Kitchen Edition",
      description: "Preserve traditional family recipes through beautifully designed recipe books and high-quality video tutorials."
    },
    {
      name: "Textile & Artifact Curation",
      description: "Professional cleaning, preservation, and repair of sentimental clothing, jewelry, and heirlooms through our specialist network."
    }],

    image: STOCK_IMAGES.heirloom,
    imageAlt: "Antique heirloom items"
  }];


  return (
    <section id="services" className="py-24 md:py-32 relative" data-testid="services-section">
      <div className="absolute inset-0 paper-texture" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <AnimatedSection className="text-center mb-20">
          <motion.p
            variants={fadeInUp}
            className="font-mono text-sm uppercase tracking-widest opacity-60 mb-4">


          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-light"
            data-testid="services-headline">

            What We Preserve
          </motion.h2>
        </AnimatedSection>
        
        <div className="space-y-32">
          {serviceCategories.map((category, idx) =>
          <ServiceCategory
            key={category.number}
            {...category}
            reversed={idx % 2 !== 0} />

          )}
        </div>
      </div>
    </section>);

};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    service: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! We'll be in touch soon.", {
        description: "Our team will reach out within 24-48 hours."
      });
      setFormData({ name: "", email: "", phone: "", message: "", service: "" });
    } catch (error) {
      toast.error("Something went wrong", {
        description: "Please try again or contact us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#F3EFE7] relative" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection>
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left: Contact Info */}
            <div>
              <motion.p
                variants={fadeInUp}
                className="font-mono text-sm uppercase tracking-widest opacity-60 mb-4">

                Get In Touch
              </motion.p>
              
              <motion.h2
                variants={fadeInUp}
                className="font-heading text-4xl md:text-5xl font-light mb-8"
                data-testid="contact-headline">

                Begin Your
                <br />
                <span className="text-[#8B4513]">Preservation Journey</span>
              </motion.h2>
              
              <motion.p
                variants={fadeInUp}
                className="text-lg leading-relaxed opacity-80 mb-12">

                Every family's story is unique. Let's discuss how we can help 
                preserve yours. Reach out to schedule a free consultation.
              </motion.p>
              
              <div className="space-y-4">
                <motion.a
                  variants={fadeInUp}
                  href="mailto:hello@yaadein.io"
                  className="contact-info-card"
                  data-testid="contact-email">

                  <div className="w-12 h-12 bg-[#2C2420] flex items-center justify-center">
                    <Mail size={20} className="text-[#FDFBF7]" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest opacity-60">Email</p>
                    <p className="font-heading text-lg">hello@yaadein.io</p>
                  </div>
                </motion.a>
              </div>
            </div>
            
            {/* Right: Contact Form */}
            <motion.div variants={fadeInUp}>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest opacity-60 block mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Enter your full name"
                    data-testid="form-name" />

                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="font-mono text-xs uppercase tracking-widest opacity-60 block mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="your@email.com"
                      data-testid="form-email" />

                  </div>
                  <div>
                    <label className="font-mono text-xs uppercase tracking-widest opacity-60 block mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="+91 00000 00000"
                      data-testid="form-phone" />

                  </div>
                </div>
                
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest opacity-60 block mb-2">
                    Service Interest
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="form-input"
                    data-testid="form-service">

                    <option value="">Select a service</option>
                    <option value="consultation">Free Consultation</option>
                    <option value="preservation">Physical & Digital Preservation</option>
                    <option value="oral-history">Oral History & Storytelling</option>
                    <option value="research">Research & Ancestry Mapping</option>
                    <option value="heirloom">Heirloom Collections & Exhibits</option>
                    <option value="comprehensive">Comprehensive Package</option>
                  </select>
                </div>
                
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest opacity-60 block mb-2">
                    Your Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="form-input resize-none"
                    placeholder="Tell us about your family's story and what you'd like to preserve..."
                    data-testid="form-message" />

                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full md:w-auto disabled:opacity-50"
                  data-testid="form-submit">

                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </section>);

};

// Footer Section
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#2C2420] text-[#FDFBF7] py-16 md:py-24" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <h3 className="font-heading text-3xl mb-4">Yaadein</h3>
            <p className="opacity-70 leading-relaxed max-w-md mb-6">
              Preserving the voices, photographs, and heirlooms that connect 
              generations—transforming scattered memories into lasting legacies.
            </p>
            <p className="font-heading text-xl italic opacity-60">
              "Every family has a story worth keeping."
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest mb-6 opacity-60">
              Navigation
            </h4>
            <ul className="space-y-3">
              <li><a href="#about" className="footer-link" data-testid="footer-about">About</a></li>
              <li><a href="#services" className="footer-link" data-testid="footer-services">Services</a></li>
              <li><a href="#contact" className="footer-link" data-testid="footer-contact">Contact</a></li>
            </ul>
          </div>
          
          {/* Services */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest mb-6 opacity-60">
              Services
            </h4>
            <ul className="space-y-3">
              <li><a href="#services" className="footer-link">Digital Preservation</a></li>
              <li><a href="#services" className="footer-link">Oral History</a></li>
              <li><a href="#services" className="footer-link">Ancestry Research</a></li>
              <li><a href="#services" className="footer-link">Heirloom Curation</a></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm opacity-50">
            © {currentYear} Yaadein. All rights reserved.
          </p>
          <p className="text-sm opacity-50">
            Crafted with care for families everywhere.
          </p>
        </div>
      </div>
    </footer>);

};

// Main App Component
function App() {
  return (
    <div className="App bg-[#FDFBF7]" data-testid="app-root">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#F3EFE7',
            border: '1px solid #D8CFC4',
            color: '#2C2420',
            fontFamily: 'DM Sans, sans-serif'
          }
        }} />

      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <ContactSection />
      </main>
      <Footer />
    </div>);

}

export default App;